import type { components } from './api.generated';

type Visitor = components['schemas']['Visitor'];
export interface SpokenMessage {
  readonly id: string;
  readonly speaker: string;
  readonly name: string;
  readonly text: string;
  readonly at: number;
  readonly kind: 'say' | 'whisper';
  readonly pose: Pick<Visitor, 'x' | 'y' | 'z' | 'mode'>;
}

export class SpeechHistory {
  private generation: string | null = null;
  private seen = new Map<string, number>();
  private lines: SpokenMessage[] = [];
  revision = 0;

  get entries(): readonly SpokenMessage[] {
    return this.lines;
  }

  active(now: number): readonly SpokenMessage[] {
    this.lines = this.lines.filter((line) => now - line.at < 5000);
    for (const [id, at] of this.seen) if (now - at >= 5000) this.seen.delete(id);
    return this.lines;
  }

  silence(speaker: string): void {
    this.lines = this.lines.filter((line) => line.speaker !== speaker);
    this.revision++;
  }

  observe(generation: string, visitors: readonly Visitor[]): void {
    if (generation !== this.generation) {
      this.generation = generation;
      this.seen.clear();
      this.lines = [];
      this.revision++;
    }
    const fresh: SpokenMessage[] = [];
    for (const visitor of visitors) {
      const speech = visitor.speech;
      if (!speech || this.seen.has(speech.id)) continue;
      this.seen.set(speech.id, speech.created_at_ms);
      fresh.push({
        id: speech.id,
        speaker: visitor.id,
        name: visitor.name,
        text: speech.text,
        at: speech.created_at_ms,
        kind: speech.kind,
        pose: { x: visitor.x, y: visitor.y, z: visitor.z, mode: visitor.mode },
      });
    }
    if (!fresh.length) return;
    this.lines = [...this.lines, ...fresh].sort((a, b) => a.at - b.at);
    const newest = Math.max(...fresh.map((line) => line.at));
    this.active(newest);
    this.revision++;
  }
}
